import { useEffect, useMemo, useState } from "react";
import { Card, CircularProgress, FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiAutocomplete from "components/AutoComplete";
import HotelSelector from "components/HotelSelector";
import KpiCard from "components/KpiCard";
import CardSkeleton from "components/Skeletons/CardSkeleton";
import Snackbar from "components/Snackbar";
import VuiTooltip from "components/VuiTooltip";

import {
  FaBroom,
  FaCheckCircle,
  FaChevronDown,
  FaDoorOpen,
  FaExclamationTriangle,
} from "react-icons/fa";

import useCommon from "hooks/useCommon";
import usePermissions from "hooks/usePermissions";

import HousekeepingRoomCard from "./components/HousekeepingRoomCard";
import RoomDetailDialog from "./components/RoomDetailDialog";
import AssignRoomDialog from "./components/AssignRoomDialog";
import UpdateRoomStatusDialog from "./components/UpdateRoomStatusDialog";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import { HOUSEKEEPING_STATUS_FILTERS, OCCUPANCY_STATES, RESOURCE_STATES } from "./constants";

import {
  getHousekeepingCounters,
  getHousekeepingPie,
  getHousekeepingRooms,
  saveHousekeepingRoomNote,
  updateHousekeepingRoomStatus,
} from "store/slice/housekeepingSlice";

const ROOM_SCOPE_FILTERS = [
  {
    label: "Arrivals",
    value: "arrival",
    tootTip: "Rooms with guests arriving today",
  },
  {
    label: "Departures",
    value: "departure",
    tootTip: "Rooms with guests departing today",
  },
  {
    label: "In-House Guests",
    value: "inhouse",
    tootTip: "Rooms with guests currently staying at the hotel",
  },
  {
    label: "All Rooms",
    value: "all",
    tootTip: "All rooms in the hotel",
  },
];

const mapResourceState = (status) => {
  switch (status) {
    case "dirty":
      return RESOURCE_STATES.DIRTY;

    case "clean":
      return RESOURCE_STATES.CLEAN;

    case "inspected":
      return RESOURCE_STATES.INSPECTED;

    case "out_of_service":
      return RESOURCE_STATES.OUT_OF_SERVICE;

    case "out_of_order":
      return RESOURCE_STATES.OUT_OF_ORDER;

    default:
      return status;
  }
};

const mapResourceStateToAPI = (status) => {
  switch (status) {
    case RESOURCE_STATES.DIRTY:
      return "dirty";

    case RESOURCE_STATES.CLEAN:
      return "clean";

    case RESOURCE_STATES.INSPECTED:
      return "inspected";

    case RESOURCE_STATES.OUT_OF_SERVICE:
      return "out_of_service";

    case RESOURCE_STATES.OUT_OF_ORDER:
      return "out_of_order";

    default:
      return status;
  }
};

const mapOccupancyState = (status) => {
  switch (status) {
    case "vacant":
      return OCCUPANCY_STATES.VACANT;

    case "reserved":
      return OCCUPANCY_STATES.RESERVED;

    case "reserved_locked":
      return OCCUPANCY_STATES.RESERVED_LOCKED;

    case "internal_use":
      return OCCUPANCY_STATES.INTERNAL_USE;

    case "out_of_order":
      return OCCUPANCY_STATES.OUT_OF_ORDER;

    case "occupied":
      return "Occupied";

    default:
      return status;
  }
};

function HousekeepingHub() {
  const { STORE, DISPATCH } = useCommon();
  const { isHouseKeeper } = usePermissions();

  const { primary, dark, light, black } = colors;

  const hotelIds = STORE((state) => state.ui.hotelIds);

  const {
    rooms,
    page,
    pageSize,
    total,
    roomsLoading,
    roomsError,

    counters,
    countersLoading,

    pie,
    pieLoading,

    updateStatusLoading,
    updateStatusError,
    updateStatusSuccess,

    saveNoteLoading,
    saveNoteError,
  } = STORE((state) => state.housekeeping);

  const roomRefreshTrigger = STORE((state) => state.pusher.roomRefreshTrigger);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [roomScope, setRoomScope] = useState("arrival");

  /*
   * Housekeepers should initially see their own rooms.
   * Everyone else should initially see all rooms.
   */
  const [statusFilter, setStatusFilter] = useState(isHouseKeeper ? "My Rooms" : "All");

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    show: false,
    color: "success",
    title: "",
    content: "",
    icon: "check",
  });

  const hotelId = hotelIds?.[0];

  /*
   * Housekeepers get an additional "My Rooms" filter.
   *
   * Other users get the normal housekeeping status filters.
   */
  const housekeepingFilters = useMemo(() => {
    if (isHouseKeeper) {
      return [
        {
          label: "My Rooms",
          value: "my_rooms",
        },
        ...HOUSEKEEPING_STATUS_FILTERS,
      ];
    }

    return HOUSEKEEPING_STATUS_FILTERS;
  }, [isHouseKeeper]);

  const selectedStatusOption =
    housekeepingFilters.find((item) => item.label === statusFilter) || null;

  /*
   * Convert selected frontend status into the tab value
   * expected by the housekeeping rooms API.
   */
  const selectedTab = useMemo(() => {
    const selected = housekeepingFilters.find((item) => item.label === statusFilter);

    if (!selected?.value) {
      return "all";
    }

    if (selected.value === "my_rooms") {
      return "my_rooms";
    }

    const statusMap = {
      [RESOURCE_STATES.DIRTY]: "dirty",
      [RESOURCE_STATES.CLEAN]: "clean",
      [RESOURCE_STATES.INSPECTED]: "inspected",
      [RESOURCE_STATES.OUT_OF_SERVICE]: "out_of_service",
      [RESOURCE_STATES.OUT_OF_ORDER]: "out_of_order",
    };

    return statusMap[selected.value] || "all";
  }, [housekeepingFilters, statusFilter]);

  /*
   * Normalize backend room data into the structure
   * expected by housekeeping components.
   */
  const normalizedRooms = useMemo(() => {
    return (rooms || []).map((room) => ({
      ...room,

      roomNumber: room.roomName,
      roomName: room.roomName,

      resourceState: mapResourceState(room.hkStatus),

      occupancyState: mapOccupancyState(room.occupancyStatus),

      assignedStaff: room.assignedUserId
        ? {
            id: room.assignedUserId,
            name: room.assignedUserName || "",
          }
        : null,

      guestName: room.guestName || "",

      note: {
        text: room.noteText || "",
        lastActorName: room.noteLastActorName || "",
        updatedAt: room.noteUpdatedAt || null,
      },

      guestFlag:
        room.flagTitle || room.flagReason
          ? {
              type: room.flagKey || "",
              reason: room.flagReason || "",
            }
          : null,

      arrivalDate: room.arrivalDate || null,
      departureDate: room.departureDate || null,
    }));
  }, [rooms]);

  const showSnackbar = (data) => {
    setSnackbar({
      show: true,
      ...data,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      show: false,
    }));
  };

  /*
   * Reset default room status whenever the role changes.
   *
   * Housekeeper -> My Rooms
   * Others      -> All
   */
  useEffect(() => {
    setStatusFilter(isHouseKeeper ? "My Rooms" : "All");
  }, [isHouseKeeper]);

  /*
   * Search debounce.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = search.trim();

      if (value.length === 0 || value.length >= 3) {
        setDebouncedSearch(value);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!hotelId) return;

    DISPATCH(
      getHousekeepingRooms({
        hotelId,
        tab: selectedTab,
        reservationFilter: roomScope,
        search: debouncedSearch || undefined,
        page: 1,
        pageSize: 20,
      })
    );
  }, [DISPATCH, hotelId, selectedTab, roomScope, debouncedSearch, roomRefreshTrigger]);

  /*
   * COUNTERS
   *
   */
  useEffect(() => {
    if (!hotelId) return;

    DISPATCH(
      getHousekeepingCounters({
        hotelId,
        reservationFilter: roomScope,
      })
    );
  }, [DISPATCH, hotelId, roomScope, updateStatusSuccess]);

  /*
   * COMPLETION / PIE
   */
  useEffect(() => {
    if (!hotelId) return;

    DISPATCH(
      getHousekeepingPie({
        hotelId,
        reservationFilter: roomScope,
      })
    );
  }, [DISPATCH, hotelId, roomScope, updateStatusSuccess]);

  /*
   * Current completion percentage.
   */
  const totalRooms = pie?.total || 0;
  const cleanRooms = pie?.clean || 0;
  const dirtyRooms = pie?.dirty || 0;
  const completionPercentage = pie?.cleanedPercentage || 0;

  const hasMore = (rooms?.length || 0) < (total || 0);

  const loadMore = () => {
    if (roomsLoading || !hasMore || !hotelId) {
      return;
    }

    DISPATCH(
      getHousekeepingRooms({
        hotelId,
        tab: selectedTab,
        reservationFilter: roomScope,
        search: debouncedSearch || undefined,
        page: page + 1,
        pageSize,
      })
    );
  };

  const handleViewRoom = (room) => {
    setSelectedRoom(room);
    setDetailOpen(true);
  };

  /*
   * Housekeepers cannot manually assign rooms.
   *
   * Other roles using this screen can access
   * the assignment workflow.
   */
  const handleOpenAssignment = (room) => {
    if (isHouseKeeper) return;

    setSelectedRoom(room);
    setAssignOpen(true);
  };

  const handleOpenStatusDialog = (room) => {
    setSelectedRoom(room);
    setStatusDialogOpen(true);
  };

  const handleUpdateRoomStatus = async ({ roomId, resourceState, version }) => {
    if (!hotelId || !roomId || !resourceState || version === undefined || version === null) {
      return false;
    }

    try {
      const status = mapResourceStateToAPI(resourceState);

      await DISPATCH(
        updateHousekeepingRoomStatus({
          roomId,
          hotelId,
          status,
          version,
        })
      ).unwrap();

      setStatusDialogOpen(false);
      setSelectedRoom(null);

      showSnackbar({
        color: "success",
        title: "Room Updated",
        content: "Room housekeeping status updated successfully.",
        icon: "check",
      });

      return true;
    } catch (error) {
      return false;
    }
  };

  const handleAddNote = async ({ roomId, message, version }) => {
    if (!hotelId || !roomId || !message?.trim() || version === undefined || version === null) {
      return false;
    }

    try {
      const response = await DISPATCH(
        saveHousekeepingRoomNote({
          roomId,
          hotelId,
          text: message.trim(),
          version,
        })
      ).unwrap();

      const note = response?.note;

      setSelectedRoom((prev) =>
        prev?.id === roomId
          ? {
              ...prev,

              note: note || {
                text: message.trim(),
              },

              version:
                response?.version !== undefined && response?.version !== null
                  ? response.version
                  : prev.version,
            }
          : prev
      );

      // showSnackbar({
      //   color: "success",
      //   title: "Note Saved",
      //   content: "Housekeeping note saved successfully.",
      //   icon: "check",
      // });

      return true;
    } catch (error) {
      showSnackbar({
        color: "error",
        title: "Unable to Save Note",
        content:
          typeof error === "string" ? error : error?.message || "Failed to save housekeeping note.",
        icon: "warning",
      });

      return false;
    }
  };

  const showSkeletons = roomsLoading && page === 1;

  const isEmpty = !roomsLoading && normalizedRooms.length === 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={0}>
        <Card>
          {/* Header */}
          <VuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <VuiBox>
              <VuiTypography variant="lg">Housekeeping Hub</VuiTypography>

              {isHouseKeeper && (
                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.3,
                    opacity: 0.6,
                  }}
                >
                  View and manage rooms assigned to you
                </VuiTypography>
              )}
            </VuiBox>

            <VuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <HotelSelector />

              <VuiBox sx={{ width: 220 }}>
                <VuiInput
                  gradient
                  placeholder="Search by room or staff name..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </VuiBox>
            </VuiBox>
          </VuiBox>

          {/* KPI Cards */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Cleaned Rooms"
                value={countersLoading ? "..." : counters?.cleanedRooms || 0}
                icon={<FaCheckCircle size={16} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Dirty Rooms"
                value={countersLoading ? "..." : counters?.dirtyRooms || 0}
                icon={<FaBroom size={16} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Vacant Rooms"
                value={countersLoading ? "..." : counters?.vacantRooms || 0}
                icon={<FaDoorOpen size={16} />}
              />
            </Grid>

            {/* Room Completion */}
            <Grid item xs={12} sm={6} md={3}>
              <VuiBox
                height="100%"
                px={2}
                py={1.5}
                borderRadius="18px"
                display="flex"
                alignItems="center"
                gap={2}
                sx={{
                  border: `1px solid ${rgba(black.main, 0.25)}`,
                }}
              >
                <VuiBox position="relative" display="inline-flex" flexShrink={0}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={66}
                    thickness={4}
                    sx={{
                      color: "rgba(28, 28, 28, 0.08)",
                    }}
                  />

                  <CircularProgress
                    variant="determinate"
                    value={pieLoading ? 0 : completionPercentage}
                    size={66}
                    thickness={4}
                    sx={{
                      color: "#22c55e",
                      position: "absolute",
                      left: 0,
                    }}
                  />

                  <VuiBox
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <VuiTypography fontWeight="regular" sx={{ fontSize: "16px" }}>
                      {pieLoading ? "..." : `${completionPercentage}%`}
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>

                <VuiBox flex={1} minWidth={0} display="flex" flexDirection="column" gap={1}>
                  <VuiTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      opacity: 0.6,
                    }}
                  >
                    Room Completion
                  </VuiTypography>

                  <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <VuiBox display="flex" alignItems="center" gap={0.7}>
                      <VuiBox
                        width={8}
                        height={8}
                        borderRadius="50%"
                        flexShrink={0}
                        sx={{
                          background: "#22c55e",
                        }}
                      />

                      <VuiTypography variant="caption"> Total</VuiTypography>
                    </VuiBox>

                    <VuiTypography variant="caption" fontWeight="medium">
                      {totalRooms}
                    </VuiTypography>
                  </VuiBox>

                  <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <VuiBox display="flex" alignItems="center" gap={0.7}>
                      <VuiBox
                        width={8}
                        height={8}
                        borderRadius="50%"
                        flexShrink={0}
                        sx={{
                          background: "#ef4444",
                        }}
                      />

                      <VuiTypography variant="caption">Dirty</VuiTypography>
                    </VuiBox>

                    <VuiTypography variant="caption" fontWeight="medium">
                      {dirtyRooms}
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              </VuiBox>
            </Grid>
          </Grid>

          {/* Filters */}
          <VuiBox
            display="flex"
            alignItems={{
              xs: "stretch",
              md: "flex-end",
            }}
            justifyContent="space-between"
            flexDirection={{
              xs: "column",
              md: "row",
            }}
            gap={2}
            my={1}
            px={2}
          >
            <VuiBox>
              <VuiTypography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 0.5,
                  opacity: 0.6,
                }}
              >
                View Rooms By
              </VuiTypography>

              <RadioGroup
                row
                value={roomScope}
                onChange={(event) => {
                  setRoomScope(event.target.value);
                }}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "13px !important",
                    fontWeight: "400 !important",
                  },
                }}
              >
                {ROOM_SCOPE_FILTERS.map((filter) => (
                  <VuiTooltip key={filter.value} title={filter.tootTip}>
                    <FormControlLabel
                      value={filter.value}
                      control={<Radio size="small" />}
                      label={filter.label}
                    />
                  </VuiTooltip>
                ))}
              </RadioGroup>
            </VuiBox>

            <VuiBox
              sx={{
                width: {
                  xs: "100%",
                  sm: 240,
                },
              }}
            >
              <VuiTypography
                variant="caption"
                ml={1}
                sx={{
                  display: "block",
                  mb: 0.4,
                }}
              >
                Room Status
              </VuiTypography>

              <VuiAutocomplete
                options={housekeepingFilters}
                value={selectedStatusOption}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                placeholder="Select room status"
                gradient
                disableClearable
                onChange={(_, selected) => {
                  if (!selected) return;

                  setStatusFilter(selected.label);
                }}
              />
            </VuiBox>
          </VuiBox>

          {/* Error */}
          {roomsError && (
            <VuiBox
              mb={2}
              p={2}
              display="flex"
              alignItems="center"
              gap={1}
              borderRadius="lg"
              sx={{
                border: "1px solid rgba(255,0,0,0.3)",
                background: "rgba(255,0,0,0.05)",
              }}
            >
              <FaExclamationTriangle size={14} color="#ef4444" />

              <VuiTypography variant="caption" color="error">
                {roomsError}
              </VuiTypography>
            </VuiBox>
          )}

          {/* Rooms */}
          {!roomsError && (
            <VuiBox
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                md: "1fr 1fr",
                lg: "1fr 1fr 1fr",
                xl: "1fr 1fr 1fr 1fr",
              }}
              gap={2}
            >
              {showSkeletons ? (
                Array.from({
                  length: 6,
                }).map((_, index) => (
                  <CardSkeleton key={index} lines={4} hasHeader hasChips hasFooter />
                ))
              ) : (
                <>
                  {normalizedRooms.map((room) => (
                    <HousekeepingRoomCard
                      key={room.id}
                      room={room}
                      onView={handleViewRoom}
                      onAssign={!isHouseKeeper ? handleOpenAssignment : undefined}
                      onUpdateStatus={handleOpenStatusDialog}
                    />
                  ))}

                  {roomsLoading &&
                    page > 1 &&
                    Array.from({
                      length: 3,
                    }).map((_, index) => (
                      <CardSkeleton
                        key={`loading-${index}`}
                        lines={4}
                        hasHeader
                        hasChips
                        hasFooter
                      />
                    ))}
                </>
              )}
            </VuiBox>
          )}

          {/* Empty State */}
          {isEmpty && (
            <VuiBox
              py={10}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              <VuiBox
                width={70}
                height={70}
                mb={2}
                borderRadius="50%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  background: light.secondary,
                  border: `1px solid ${dark.main}`,
                }}
              >
                <FaBroom size={20} color={primary.main} />
              </VuiBox>

              <VuiTypography variant="h5" fontWeight="medium" mb={0.5}>
                No Rooms Found
              </VuiTypography>

              <VuiTypography
                variant="caption"
                sx={{
                  maxWidth: 380,
                }}
              >
                No rooms match the selected room type, status, or search.
              </VuiTypography>
            </VuiBox>
          )}

          {/* Load More */}
          {!showSkeletons && normalizedRooms.length > 0 && hasMore && (
            <VuiBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              mt={3}
              onClick={loadMore}
              sx={{
                cursor: roomsLoading ? "default" : "pointer",
                userSelect: "none",
                width: "fit-content",
                mx: "auto",
                opacity: roomsLoading ? 0.5 : 0.9,
                transition: "all 0.2s ease",

                "&:hover": {
                  opacity: roomsLoading ? 0.5 : 1,
                  transform: roomsLoading ? "none" : "translateY(1px)",
                },
              }}
            >
              <VuiTypography
                variant="caption"
                sx={{
                  letterSpacing: "0.3px",
                }}
              >
                {roomsLoading ? "Loading..." : "Load More"}
              </VuiTypography>

              {!roomsLoading && <FaChevronDown size={12} />}
            </VuiBox>
          )}
        </Card>
      </VuiBox>

      {/* Room Details */}
      {detailOpen && (
        <RoomDetailDialog
          open={detailOpen}
          room={selectedRoom}
          onClose={() => {
            setDetailOpen(false);
            setSelectedRoom(null);
          }}
          onAssign={
            !isHouseKeeper
              ? () => {
                  setDetailOpen(false);
                  setAssignOpen(true);
                }
              : undefined
          }
          onUpdateStatus={() => {
            setDetailOpen(false);
            setStatusDialogOpen(true);
          }}
          onAddNote={handleAddNote}
        />
      )}

      {/* Assignment Dialog
          Housekeepers do not get manual assignment.
      */}
      {!isHouseKeeper && assignOpen && (
        <AssignRoomDialog
          open={assignOpen}
          room={selectedRoom}
          onClose={() => {
            setAssignOpen(false);
            setSelectedRoom(null);
          }}
        />
      )}

      {/* Status Dialog */}
      {statusDialogOpen && (
        <UpdateRoomStatusDialog
          open={statusDialogOpen}
          room={selectedRoom}
          onClose={() => {
            if (updateStatusLoading) return;

            setStatusDialogOpen(false);
            setSelectedRoom(null);
          }}
          onUpdate={handleUpdateRoomStatus}
        />
      )}

      <Snackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.show}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </DashboardLayout>
  );
}

export default HousekeepingHub;
